import mapboxgl from 'mapbox-gl';
import { VIEWPORT_DEFAULT } from './data-sources';

/** Recorre coordenadas GeoJSON y extiende el bounding box. */
function extendBoundsFromGeoCoords(bounds: mapboxgl.LngLatBounds, coords: unknown): void {
  if (!Array.isArray(coords) || coords.length === 0) return;
  const first = coords[0];
  if (typeof first === 'number' && typeof coords[1] === 'number') {
    bounds.extend(coords as [number, number]);
    return;
  }
  for (const c of coords) {
    extendBoundsFromGeoCoords(bounds, c);
  }
}

/**
 * Computa bounds desde una source de Mapbox. Devuelve null si no hay
 * features o el source no existe. Eficiente: solo extiende coords de las
 * features ya tileadas (querySourceFeatures es O(N) sobre lo cargado).
 */
function boundsFromSource(map: mapboxgl.Map, sourceId: string): mapboxgl.LngLatBounds | null {
  if (!map.getSource(sourceId)) return null;
  let features: mapboxgl.MapboxGeoJSONFeature[];
  try {
    features = map.querySourceFeatures(sourceId);
  } catch {
    return null;
  }
  if (!features.length) return null;
  const bounds = new mapboxgl.LngLatBounds();
  for (const f of features) {
    const g = f.geometry;
    if (!g || g.type === 'GeometryCollection') continue;
    if ('coordinates' in g) {
      extendBoundsFromGeoCoords(bounds, g.coordinates as unknown);
    }
  }
  return bounds.isEmpty() ? null : bounds;
}

/**
 * Ajusta la cámara al área del proyecto.
 *
 * Estrategia, ordenada por costo y precisión:
 *   1. AOI (`aoi` source) — polígono único del área de interés. ~30 vertices.
 *      O(N) trivial. Es el "contorno exterior" que pidió el user.
 *   2. AOI San Pedro (`aoi_san_pedro`) si el AOI base no aporta bounds.
 *   3. Parcelas (`parcelas` source) — fallback si AOI aún no carga.
 *      ~2400 features pero solo iteramos las tileadas en viewport actual.
 *   4. Reintento on idle si nada está listo.
 *   5. Viewport default (centro Huimilpan/Corregidora) como último recurso.
 *
 * Padding 56px + maxZoom 16 evita zoom excesivo cuando el area es chica.
 */
export function fitMapToAreaOfInterest(map: mapboxgl.Map): void {
  const tryFit = (): boolean => {
    // Preferir AOI (más eficiente y representa el "outer contour" del proyecto).
    let bounds = boundsFromSource(map, 'aoi');
    if (!bounds) bounds = boundsFromSource(map, 'aoi_san_pedro');
    // Fallback: si AOI no está disponible, usar bounds de parcelas. Más
    // inclusivo si el user agrega parcelas fuera del AOI original.
    if (!bounds) bounds = boundsFromSource(map, 'parcelas');
    if (!bounds) return false;
    map.fitBounds(bounds, { padding: 56, duration: 1000, maxZoom: 16 });
    return true;
  };

  if (tryFit()) return;

  const retryOrFallback = () => {
    if (!tryFit()) {
      map.flyTo({
        center: VIEWPORT_DEFAULT.centro,
        zoom: VIEWPORT_DEFAULT.zoom,
        duration: 900,
      });
    }
  };

  if (map.loaded()) {
    map.once('idle', retryOrFallback);
  } else {
    map.once('load', () => map.once('idle', retryOrFallback));
  }
}
